import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShoppingCart, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import wallpaper from "../../../../assets/images/domain/wallpaper.webp";
import wallpaper1 from "../../../../assets/images/domain/flower-wallpaper.webp";
import wallpaper2 from "../../../../assets/images/domain/flower-wallpaper1.webp";

const CAROUSEL_IMAGES = [
    wallpaper,
    wallpaper1,
    wallpaper2,
];

// ── Typing animation hook ──────────────────────────────────────────────
const WORDS = ["your-domain", "mybusiness", "shoplocal", "techstartup", "creative-co"];
const TLD_LIST = [".au", ".shop", ".store", ".net", ".io"];

function useTypingCycle() {
    const [wordIdx, setWordIdx] = useState(0);
    const [displayed, setDisplayed] = useState("");
    const [phase, setPhase] = useState<"typing" | "pause" | "erasing">("typing");
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const word = WORDS[wordIdx];
        const schedule = (fn: () => void, delay: number) => {
            timeoutRef.current = setTimeout(fn, delay);
        };

        if (phase === "typing") {
            if (displayed.length < word.length) {
                schedule(() => setDisplayed(word.slice(0, displayed.length + 1)), 90);
            } else {
                schedule(() => setPhase("pause"), 1400);
            }
        } else if (phase === "pause") {
            schedule(() => setPhase("erasing"), 0);
        } else {
            if (displayed.length > 0) {
                schedule(() => setDisplayed(displayed.slice(0, -1)), 55);
            } else {
                setWordIdx((i) => (i + 1) % WORDS.length);
                setPhase("typing");
            }
        }

        return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    }, [displayed, phase, wordIdx]);

    return { displayed };
}

// ── Domain Search Mockup ───────────────────────────────────────────────
const DomainSearchMockup = () => {
    const { displayed } = useTypingCycle();

    const tldEntries = TLD_LIST.map((tld, i) => ({
        tld,
        visible: displayed.length >= 3,
        delay: i * 80,
    }));

    return (
        <div className="relative w-full h-full flex items-center justify-center p-6 sm:p-8">
            {/* Dark glass card */}
            <div
                className="rounded-2xl p-4 sm:p-5 w-64 sm:w-72 relative z-10"
                style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    backdropFilter: "blur(16px)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
                }}
            >
                {/* Search bar */}
                <div
                    className="flex items-center gap-2 rounded-full px-3 sm:px-4 py-2 sm:py-2.5 mb-4"
                    style={{
                        background: "rgba(255,255,255,0.07)",
                        border: "1px solid rgba(255,255,255,0.12)",
                    }}
                >
                    <span
                        className="text-xs sm:text-sm font-medium tracking-tight min-w-0 truncate"
                        style={{ minHeight: "1.2em", color: "#e5e7eb" }}
                    >
                        {displayed}
                    </span>
                    <span style={{ color: "#9ca3af", animation: "blink 1s step-end infinite" }} className="select-none">|</span>
                    <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-auto shrink-0" style={{ color: "#6b7280" }} />
                </div>

                {/* TLD rows */}
                <div className="space-y-2.5 sm:space-y-3">
                    {tldEntries.map(({ tld, visible, delay }, i) => (
                        <div
                            key={tld}
                            className="flex items-center justify-between transition-all duration-500"
                            style={{
                                opacity: visible ? (i === 0 ? 1 : Math.max(0.12, 0.4 - i * 0.08)) : 0,
                                transform: visible ? "translateY(0)" : "translateY(6px)",
                                transitionDelay: `${delay}ms`,
                            }}
                        >
                            <span
                                className="text-xs sm:text-sm truncate mr-2"
                                style={{ color: i === 0 ? "#f3f4f6" : "#6b7280" }}
                            >
                                {displayed || WORDS[0]}
                                <strong className="font-bold" style={{ color: i === 0 ? "#a78bfa" : "inherit" }}>{tld}</strong>
                            </span>
                            {i === 0 && (
                                <div
                                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shrink-0"
                                    style={{
                                        background: "rgba(45,212,191,0.15)",
                                        border: "1px solid rgba(45,212,191,0.3)",
                                    }}
                                >
                                    <ShoppingCart className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: "#2dd4bf" }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Domain Secured badge */}
            <div
                className="absolute bottom-6 sm:bottom-10 right-4 sm:right-8 rounded-2xl px-3 sm:px-5 py-2.5 sm:py-4 flex items-center gap-2 sm:gap-3 z-20"
                style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(45,212,191,0.25)",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3), 0 0 20px rgba(45,212,191,0.08)",
                }}
            >
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 shrink-0" style={{ color: "#2dd4bf" }} strokeWidth={1.8} />
                <span className="font-semibold text-xs sm:text-sm whitespace-nowrap" style={{ color: "#f3f4f6" }}>
                    Domain Secured
                </span>
            </div>

            <style>{`
                @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50%       { opacity: 0; }
                }
            `}</style>
        </div>
    );
};

// ── Sliding Image Carousel ─────────────────────────────────────────────
const slideStyles = `
    @keyframes slideInLeft   { from { transform: translateX(100%);  } to { transform: translateX(0); } }
    @keyframes slideInRight  { from { transform: translateX(-100%); } to { transform: translateX(0); } }
    @keyframes slideOutLeft  { from { transform: translateX(0); } to { transform: translateX(-100%);  } }
    @keyframes slideOutRight { from { transform: translateX(0); } to { transform: translateX(100%);   } }
`;

const DotCoMockup = () => {
    const total = CAROUSEL_IMAGES.length;
    const [current, setCurrent] = useState(0);
    const [prev, setPrev] = useState<number | null>(null);
    const [direction, setDirection] = useState<"left" | "right">("left");
    const [animating, setAnimating] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const goTo = (next: number, dir: "left" | "right") => {
        if (animating || next === current) return;
        setDirection(dir);
        setPrev(current);
        setCurrent(next);
        setAnimating(true);
        setTimeout(() => { setPrev(null); setAnimating(false); }, 600);
    };

    const goNext = () => goTo((current + 1) % total, "left");
    const goPrev = () => goTo((current - 1 + total) % total, "right");

    useEffect(() => {
        intervalRef.current = setInterval(goNext, 3500);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [current, animating]);

    const inAnim  = direction === "left" ? "slideInLeft"  : "slideInRight";
    const outAnim = direction === "left" ? "slideOutLeft" : "slideOutRight";

    return (
        <div className="relative w-full h-full flex items-center justify-center p-6 sm:p-8">
            <style>{slideStyles}</style>

            {/* Browser frame */}
            <div
                className="w-full max-w-xs sm:max-w-sm rounded-xl overflow-hidden aspect-[4/3] flex flex-col"
                style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                }}
            >
                {/* Chrome bar */}
                <div
                    className="px-3 py-2 flex items-center gap-1.5 shrink-0"
                    style={{ background: "rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}
                >
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.15)" }} />
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.15)" }} />
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.15)" }} />
                </div>

                {/* Sliding images */}
                <div className="flex-1 relative overflow-hidden">
                    {prev !== null && (
                        <img
                            key={`out-${prev}`}
                            src={CAROUSEL_IMAGES[prev]}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{
                                animation: `${outAnim} 0.6s cubic-bezier(0.4,0,0.2,1) forwards`,
                                filter: "brightness(0.7) saturate(0.8)",
                            }}
                        />
                    )}
                    <img
                        key={`in-${current}`}
                        src={CAROUSEL_IMAGES[current]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                            animation: animating ? `${inAnim} 0.6s cubic-bezier(0.4,0,0.2,1) forwards` : "none",
                            filter: "brightness(0.7) saturate(0.8)",
                        }}
                    />
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ background: "rgba(0,0,0,0.2)" }}
                    />
                </div>
            </div>

            {/* .au pill badge */}
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-8 rounded-full px-4 sm:px-6 py-1.5 sm:py-2 flex items-center gap-2 z-20 pointer-events-none"
                style={{
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                }}
            >
                <div className="w-10 sm:w-16 h-0.5 rounded" style={{ background: "rgba(255,255,255,0.2)" }} />
                <span className="font-bold text-sm" style={{ color: "#f9fafb" }}>.com.au</span>
            </div>

            {/* Prev / Next arrows */}
            <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center z-30 transition-all hover:scale-110"
                style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(8px)",
                }}
                aria-label="Previous image"
            >
                <ChevronLeft className="w-4 h-4" style={{ color: "#e5e7eb" }} />
            </button>
            <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center z-30 transition-all hover:scale-110"
                style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(8px)",
                }}
                aria-label="Next image"
            >
                <ChevronRight className="w-4 h-4" style={{ color: "#e5e7eb" }} />
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">
                {CAROUSEL_IMAGES.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => goTo(i, i > current ? "left" : "right")}
                        className="rounded-full transition-all duration-300"
                        style={{
                            width: i === current ? "16px" : "6px",
                            height: "6px",
                            background: i === current ? "#a78bfa" : "rgba(255,255,255,0.3)",
                        }}
                        aria-label={`Go to image ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

// ── Main Export ────────────────────────────────────────────────────────
export default function BusinessSection() {
    const navigate = useNavigate();

    // ✅ Scrolls to #domain-search on the /domains page
    // If already on /domains, scrolls immediately.
    // If on another page, navigates first then scrolls after mount.
    const goToSearch = () => {
        const el = document.getElementById("domain-search");
        if (el) {
            // Already on the domains page — just smooth scroll
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            // Navigate to /domains, then scroll once the page has mounted
            navigate("/domains");
            setTimeout(() => {
                document.getElementById("domain-search")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 150);
        }
    };

    return (
        <section
            className="container py-12 sm:py-16"
            style={{ background: "transparent" }}
        >
            {/* Section heading */}
            <h1
                className="text-center text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-8 sm:mb-12 lg:mb-16 px-2"
                style={{ color: "#f9fafb" }}
            >
                Spark that starts your business?
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">

                {/* Left — Domains */}
                <div
                    className="rounded-3xl overflow-hidden flex flex-col min-h-[400px] sm:min-h-[460px] relative"
                    style={{
                        background: "linear-gradient(145deg, rgba(45,212,191,0.08) 0%, rgba(255,255,255,0.03) 100%)",
                        border: "1px solid rgba(45,212,191,0.15)",
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.03), 0 8px 32px rgba(0,0,0,0.3)",
                    }}
                >
                    <div
                        className="absolute top-0 left-[15%] right-[15%] h-px"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(45,212,191,0.5), transparent)" }}
                    />

                    <div className="flex-1 min-h-[220px] sm:min-h-0">
                        <DomainSearchMockup />
                    </div>

                    <div className="p-6 sm:p-8 pt-0">
                        <h2
                            className="text-xl sm:text-2xl font-bold mb-3"
                            style={{ color: "#f9fafb" }}
                        >
                            Domains
                        </h2>
                        <p
                            className="leading-relaxed mb-4 sm:mb-5"
                            style={{ color: "#9ca3af" }}
                        >
                            Get started with the perfect domain, complete with free lifetime domain privacy protection.
                        </p>
                        <button
                            onClick={goToSearch}
                            className="font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all duration-150 active:scale-95"
                            style={{
                                background: "rgba(45,212,191,0.15)",
                                border: "1px solid rgba(45,212,191,0.3)",
                                color: "#2dd4bf",
                                boxShadow: "0 4px 16px rgba(45,212,191,0.1)",
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = "rgba(45,212,191,0.25)";
                                e.currentTarget.style.boxShadow = "0 4px 20px rgba(45,212,191,0.2)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = "rgba(45,212,191,0.15)";
                                e.currentTarget.style.boxShadow = "0 4px 16px rgba(45,212,191,0.1)";
                            }}
                        >
                            Search Domains
                        </button>
                    </div>
                </div>

                {/* Right — .au deal */}
                <div
                    className="rounded-3xl overflow-hidden flex flex-col min-h-[400px] sm:min-h-[460px] relative"
                    style={{
                        background: "linear-gradient(145deg, rgba(139,92,246,0.1) 0%, rgba(37,99,235,0.06) 100%)",
                        border: "1px solid rgba(139,92,246,0.18)",
                        boxShadow: "0 0 0 1px rgba(255,255,255,0.03), 0 8px 32px rgba(0,0,0,0.3)",
                    }}
                >
                    <div
                        className="absolute top-0 left-[15%] right-[15%] h-px"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.6), transparent)" }}
                    />

                    <div className="flex-1 min-h-[220px] sm:min-h-0">
                        <DotCoMockup />
                    </div>

                    <div className="p-6 sm:p-8 pt-0">
                        <h2
                            className="text-xl sm:text-2xl font-bold mb-3"
                            style={{ color: "#f9fafb" }}
                        >
                            .com.au for $0.05/1st year
                        </h2>
                        <p
                            className="leading-relaxed"
                            style={{ color: "#9ca3af" }}
                        >
                            Make your company and website stand out with a .com.au domain.
                        </p>
                        <p
                            className="leading-relaxed text-sm mb-4 sm:mb-5"
                            style={{ color: "#9ca3af" }}
                        >
                            *3-year purchase required.
                        </p>
                        <button
                            onClick={goToSearch}
                            className="font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all duration-150 active:scale-95"
                            style={{
                                background: "linear-gradient(135deg, #7c3aed, #2563eb)",
                                border: "none",
                                color: "#fff",
                                boxShadow: "0 4px 20px rgba(109,40,217,0.4)",
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.boxShadow = "0 6px 28px rgba(109,40,217,0.55)";
                                e.currentTarget.style.filter = "brightness(1.1)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.boxShadow = "0 4px 20px rgba(109,40,217,0.4)";
                                e.currentTarget.style.filter = "brightness(1)";
                            }}
                        >
                            Find Your Domain
                        </button>
                    </div>
                </div>

            </div>
        </section>
    );
}